import { Share, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import styles from './IosInstallBanner.module.scss'

const STORAGE_KEY = 'prism.ios-install-banner.dismissed'

/**
 * iOS hides "Add to Home Screen" inside the share sheet, so first-time
 * mobile-Safari visitors miss the install affordance entirely. This
 * banner nudges them with the share icon + a one-line how-to and self-
 * dismisses (persisted in localStorage). Shown only on iPhone/iPad
 * Safari outside standalone mode — anywhere else returns null.
 *
 * Android prompts itself via beforeinstallprompt; we don't reimplement
 * that here.
 */
export function IosInstallBanner() {
  const [dismissed, setDismissed] = useLocalStorage<boolean>(STORAGE_KEY, false)
  const [eligible, setEligible] = useState(false)

  useEffect(() => {
    if (dismissed) return
    if (typeof window === 'undefined') return
    const ua = window.navigator.userAgent
    const isIos = /iPhone|iPad|iPod/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
    if (!isIos) return
    // Already installed — Safari standalone exposes navigator.standalone;
    // also covers the iOS 14+ matchMedia signal.
    const standalone =
      // iOS-only navigator extension; not in lib.dom.
      (window.navigator as { standalone?: boolean }).standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches
    if (standalone) return
    setEligible(true)
  }, [dismissed])

  if (!eligible || dismissed) return null

  return (
    <div className={styles.banner} role="status" aria-live="polite">
      <div className={styles.body}>
        <strong className={styles.title}>Instale o Prism</strong>
        <span className={styles.hint}>
          Toque em <Share aria-hidden className={styles.shareIcon} /> e depois em{' '}
          <em>Adicionar à Tela de Início</em>.
        </span>
      </div>
      <button
        type="button"
        className={styles.dismiss}
        aria-label="Fechar dica de instalação"
        onClick={() => {
          setDismissed(true)
        }}
      >
        <X aria-hidden />
      </button>
    </div>
  )
}
